/**
 * Typed fetch wrapper used by the SPA.
 *
 * All requests include cookies. When the short-lived access cookie has
 * expired, a single shared refresh rotation renews it and the rejected
 * request is replayed once.
 */

import i18next from "i18next";

import { currentGuestHandle } from "@/embed/guestSession";
import { SURFACE_HEADER, currentSurface } from "@/embed/surface";
import { QUOTE_CHECKOUT_HEADER, currentQuoteCheckout } from "@/lib/quoteCheckout";
import {
  currentPreviewCartHandle,
  isCustomerPreview,
} from "@/lib/customerPreview";
import { planLimitMessage } from "@/lib/planLimitMessage";

interface ApiErrorPayload {
  error_code: string;
  params: Record<string, unknown>;
  message?: string | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly params: Record<string, unknown>;

  constructor(status: number, payload: ApiErrorPayload) {
    super(
      planLimitMessage(payload.error_code, payload.params ?? {}) ??
        payload.message ??
        payload.error_code,
    );
    this.name = "ApiError";
    this.status = status;
    this.code = payload.error_code;
    this.params = payload.params ?? {};
  }
}

/** Kolikrát smí react-query zopakovat dotaz, který selhal přechodně. */
const TRANSIENT_RETRIES = 3;

/**
 * Stavy, které JSOU výpadkem, i když jsou ve čtyřstovkách: vypršelý
 * požadavek a rate limit se za chvíli chovají jinak, takže opakovat
 * je má smysl.
 */
const RETRYABLE_CLIENT_STATUSES = new Set([408, 429]);

/**
 * Politika opakování pro react-query.
 *
 * 🪤 KAN-707 — výchozí `retry: 3` bere každý pád jako výpadek sítě, jenže
 * 4xx je DEFINITIVNÍ odpověď: server ji na týž dotaz zopakuje. Diagnostika
 * rozjezdu tenanta razerback našla v konzoli admina 12× „Failed to load
 * resource: 401" a jednu zaniklou cestu volanou 3× po sobě — čtvrtina z
 * toho byl skutečný stav, zbytek pouhé opakování, které přehlušilo chyby,
 * na kterých záleželo. 401 navíc opakovat nemá co: rotaci access tokenu
 * dělá `apiFetch` sám, o patro níž, a 401 až PO ní znamená odhlášeno.
 */
export function retryOnlyTransient(failureCount: number, error: unknown): boolean {
  if (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    !RETRYABLE_CLIENT_STATUSES.has(error.status)
  ) {
    return false;
  }
  return failureCount < TRANSIENT_RETRIES;
}

const DEFAULT_BASE_URL = "http://localhost:8000";
const REFRESH_PATH = "/api/v1/auth/refresh";
const PUBLIC_AUTH_PATHS = [
  "/auth/magic-link",
  "/auth/magic-link/verify-2fa",
  "/auth/sign-in",
  "/auth/sign-up",
  "/auth/password-reset/request",
  "/auth/password-reset/confirm",
];

// Refresh tokens are single-use. A shared promise prevents parallel 401s
// from racing to rotate the same cookie and invalidating one another —
// within one tab. Across tabs the Web Locks API serialises rotations,
// and a localStorage stamp lets a queued tab detect "someone already
// rotated while I waited" and skip its own (now pointless) rotation.
let refreshInFlight: Promise<RefreshOutcome> | null = null;
let sessionGeneration = 0;

type RefreshOutcome = "refreshed" | "unauthorized";

const REFRESH_LOCK_NAME = "dxf-session-refresh";
const REFRESH_STAMP_KEY = "dxf:session-refreshed-at";

function readRefreshStamp(): string | null {
  try {
    return localStorage.getItem(REFRESH_STAMP_KEY);
  } catch {
    return null; // storage blocked (private mode / embedded webview)
  }
}

function writeRefreshStamp(): void {
  try {
    localStorage.setItem(
      REFRESH_STAMP_KEY,
      `${Date.now()}:${Math.random()}`,
    );
  } catch {
    // storage blocked — cross-tab dedupe degrades, refresh still works
  }
}

export function getApiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_URL;
  if (typeof fromEnv !== "string") return DEFAULT_BASE_URL;
  if (fromEnv.length === 0) return "";
  return fromEnv;
}

interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  lang?: string;
  body?: unknown;
  baseUrl?: string;
  /** Surface a caller-owned 401 without attempting session renewal. */
  skipSessionRefresh?: boolean;
}

function resolveApiUrl(path: string, base: string, lang?: string): string {
  const url = base === "" ? null : new URL(path, base);
  if (lang && url) url.searchParams.set("lang", lang);
  if (url) return url.toString();

  const separator = path.includes("?") ? "&" : "?";
  return lang ? `${path}${separator}lang=${encodeURIComponent(lang)}` : path;
}

function isPublicAuthPath(path: string): boolean {
  const pathname = path.split("?", 1)[0] ?? path;
  return PUBLIC_AUTH_PATHS.some((suffix) => pathname.endsWith(suffix));
}

async function performRefresh(
  base: string,
  lang?: string,
): Promise<RefreshOutcome> {
  const stampBefore = readRefreshStamp();
  const rotate = async (): Promise<RefreshOutcome> => {
    if (readRefreshStamp() !== stampBefore) {
      // Another tab rotated while we waited for the lock — the fresh
      // access cookie already sits in the shared jar; a second POST
      // would just burn the new refresh token for nothing.
      return "refreshed";
    }
    const headers: Record<string, string> = { Accept: "application/json" };
    if (lang) headers["Accept-Language"] = lang;
    const response = await fetch(resolveApiUrl(REFRESH_PATH, base), {
      method: "POST",
      credentials: "include",
      headers,
    });
    if (response.ok) {
      writeRefreshStamp();
      return "refreshed";
    }
    if (response.status === 401 || response.status === 403) {
      return "unauthorized";
    }
    // 5xx / 429 from the refresh endpoint is a server hiccup, not a
    // sign-out — surface it instead of rendering the user anonymous.
    throw await parseApiError(response);
  };
  // Web Locks is unavailable in some embedded webviews — fall back to
  // the unserialised behaviour (the BE reuse-grace window still
  // absorbs a cross-tab race).
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (locks?.request) {
    return locks.request(REFRESH_LOCK_NAME, rotate);
  }
  return rotate();
}

async function refreshSession(
  base: string,
  lang?: string,
): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    // Rejections (network down mid-refresh, refresh endpoint 5xx)
    // propagate to every awaiting request — deliberately NOT mapped
    // to "unauthorized": a laptop waking with the radio still off
    // used to render the app signed-out even though the session was
    // perfectly fine.
    refreshInFlight = performRefresh(base, lang)
      .then((outcome) => {
        if (outcome === "refreshed") sessionGeneration += 1;
        return outcome;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

async function parseApiError(response: Response): Promise<ApiError> {
  let payload: ApiErrorPayload;
  try {
    const body = (await response.json()) as Partial<ApiErrorPayload>;
    payload = typeof body.error_code === "string"
      ? { error_code: body.error_code, params: body.params ?? {}, message: body.message }
      : { error_code: `http_${response.status}`, params: {}, message: null };
  } catch {
    payload = { error_code: `http_${response.status}`, params: {}, message: null };
  }
  return new ApiError(response.status, payload);
}

export async function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const { lang, body, headers, baseUrl, skipSessionRefresh = false, ...rest } = opts;
  const base = baseUrl ?? getApiBaseUrl();
  const url = resolveApiUrl(path, base, lang);
  const uiLang = lang ?? i18next.language;
  // The guest cart handle, when this browser holds one. Only the embed
  // (and a storefront visitor who arrived from it) ever does — see
  // ``embed/guestSession``. On the storefront proper the handle stays
  // in the ``dxf_cart_session`` cookie and this header is absent, so
  // nothing about the existing flow changes.
  // KAN-747 — v náhledu „zobrazit jako zákazník" jede tab bez session
  // cookie, takže by s ní odešel i handle košíku. Náhled má proto
  // vlastní, sessionStorage-scoped handle na hlavičkovém transportu;
  // mimo náhled je ``null`` a nic se nemění.
  const previewing = isCustomerPreview();
  const guestHandle = previewing ? currentPreviewCartHandle() : currentGuestHandle();
  // Which surface is asking — the embed on somebody's website, or the
  // storefront finishing a journey that started there. Read by the
  // funnel counters and by nothing else; see ``embed/surface.ts``.
  const surface = currentSurface();
  // KAN-1658 — pokladna vystavené nabídky: zdrojem položek je nabídka.
  const quoteCheckout = currentQuoteCheckout();
  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...(uiLang ? { "Accept-Language": uiLang } : {}),
    ...(guestHandle ? { "X-Guest-Session": guestHandle } : {}),
    ...(surface ? { [SURFACE_HEADER]: surface } : {}),
    ...(quoteCheckout ? { [QUOTE_CHECKOUT_HEADER]: quoteCheckout } : {}),
    ...(headers as Record<string, string> | undefined),
  };

  let finalBody: BodyInit | undefined;
  if (body !== undefined) {
    if (body instanceof FormData) {
      finalBody = body;
    } else {
      finalHeaders["Content-Type"] = "application/json";
      finalBody = JSON.stringify(body);
    }
  }

  // ``omit`` je celé jádro náhledu: session cookie se neodešle, takže
  // backend request vidí jako anonymní a odpovídá tím, co uvidí
  // zákazník. Bez toho by šlo jen o schované admin chrome.
  // Stojí ZA ``...rest``: volající s vlastním ``credentials`` náhled
  // nepřebije — jinak by upload odešel s cookie vlastníka a košík bez
  // ní, a díl by patřil jinému návštěvníkovi (KAN-1426).
  const request = () => fetch(url, {
    ...rest,
    credentials: previewing ? "omit" : "include",
    headers: finalHeaders,
    body: finalBody,
  });

  const requestGeneration = sessionGeneration;
  let response = await request();

  // Access JWTs intentionally expire after 15 minutes. A fresh SPA deployment
  // reload should renew a valid refresh session instead of showing the user as
  // signed out. Credential/TOTP endpoints retain their own 401 semantics.
  // V náhledu zákazníka je 401 správná odpověď, ne vypršelá session:
  // cookie jsme schválně neposlali. Rotace by session obnovila, replay
  // by ji zase neposlal a skončil na témže 401 — jen o kolo později.
  if (
    response.status === 401
    && !skipSessionRefresh
    && !previewing
    && !isPublicAuthPath(path)
  ) {
    if (requestGeneration !== sessionGeneration) {
      // Someone in this tab already rotated since our first attempt.
      response = await request();
    } else {
      // May throw (network error / refresh-endpoint 5xx) — only a
      // clean 401 from the refresh endpoint means "signed out".
      const outcome = await refreshSession(base, uiLang);
      if (outcome === "refreshed") response = await request();
    }
  }

  if (!response.ok) throw await parseApiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}